import { useEffect, useMemo, useState } from 'react';
import { collection, doc, onSnapshot, query, where } from 'firebase/firestore';
import { db } from '../services/firebase';
import type { ConsultationThread, GuidedClientAccess, HouseholdPlanMeta, Language, User } from '../types';
import { subscribeCurrentConsultantPhrase } from '../services/consultantPhraseService';
import {
    buildConsultationThreadId,
    threadHasUnreadForClient,
} from '../utils/consultationThreads';
import { getTranslation } from '../utils/translations';
import { getUserTypeLabel } from '../utils/userType';
import { isGuidedUser } from '../utils/accountMode';
import { getUserDisplayName } from '../utils/userDisplay';

export interface ConsultantBannerViewModel {
    consultantId: string;
    name: string;
    roleLabel: string;
    avatarUrl?: string;
    phraseOfTheDay?: string;
    lastPlanUpdateAt?: string;
    hasUnreadMessages: boolean;
    isReady: boolean;
}

function getInitials(name: string): string {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return '?';
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return `${parts[0][0] ?? ''}${parts[parts.length - 1][0] ?? ''}`.toUpperCase();
}

function parseHouseholdPlanMeta(raw: unknown): HouseholdPlanMeta | null {
    if (!raw || typeof raw !== 'object') return null;
    const data = raw as Partial<HouseholdPlanMeta>;
    if (typeof data.lastUpdatedAt !== 'string' || typeof data.lastUpdatedByUid !== 'string') {
        return null;
    }
    return {
        householdId: typeof data.householdId === 'string' ? data.householdId : '',
        lastUpdatedAt: data.lastUpdatedAt,
        lastUpdatedByUid: data.lastUpdatedByUid,
    };
}

function parseConsultantUser(uid: string, raw: unknown): User | null {
    if (!raw || typeof raw !== 'object') return null;
    return { ...(raw as Omit<User, 'uid'>), uid };
}

export function useConsultantBannerData(
    user: User | null,
    activeHouseholdId?: string,
    language?: Language,
): ConsultantBannerViewModel | null {
    const guided = user ? isGuidedUser(user) : false;
    const t = (key: Parameters<typeof getTranslation>[1]) => getTranslation(language, key);
    const [accessRows, setAccessRows] = useState<GuidedClientAccess[]>([]);
    const [consultantProfile, setConsultantProfile] = useState<User | null>(null);
    const [phraseText, setPhraseText] = useState<string | null>(null);
    const [thread, setThread] = useState<ConsultationThread | null>(null);
    const [planMeta, setPlanMeta] = useState<HouseholdPlanMeta | null>(null);
    const [accessLoaded, setAccessLoaded] = useState(false);

    useEffect(() => {
        if (!guided || !user?.guidedClientId) {
            setAccessRows([]);
            setAccessLoaded(false);
            return;
        }
        const q = query(
            collection(db, 'guidedClientAccess'),
            where('clientId', '==', user.guidedClientId),
            where('isActive', '==', true),
        );
        return onSnapshot(q, (snap) => {
            setAccessRows(snap.docs.map((entry) => ({
                ...(entry.data() as Omit<GuidedClientAccess, 'id'>),
                id: entry.id,
            })));
            setAccessLoaded(true);
        });
    }, [guided, user?.guidedClientId]);

    const activeAccess = useMemo(() => {
        if (!activeHouseholdId) return accessRows[0];
        return accessRows.find((row) => row.householdId === activeHouseholdId) ?? accessRows[0];
    }, [accessRows, activeHouseholdId]);

    const consultantId = activeAccess?.consultantId;

    useEffect(() => {
        if (!guided || !consultantId) {
            setConsultantProfile(null);
            return;
        }
        return onSnapshot(doc(db, 'users', consultantId), (snap) => {
            if (!snap.exists()) {
                setConsultantProfile(null);
                return;
            }
            setConsultantProfile(parseConsultantUser(consultantId, snap.data()));
        });
    }, [guided, consultantId]);

    useEffect(() => {
        if (!guided || !consultantId) {
            setPhraseText(null);
            return;
        }
        return subscribeCurrentConsultantPhrase(consultantId, (phrase) => {
            setPhraseText(phrase?.phrase ?? null);
        });
    }, [guided, consultantId]);

    useEffect(() => {
        if (!guided || !user?.guidedClientId || !consultantId) {
            setThread(null);
            return;
        }
        const threadId = buildConsultationThreadId(user.guidedClientId, consultantId);
        return onSnapshot(doc(db, 'consultationThreads', threadId), (snap) => {
            if (!snap.exists()) {
                setThread(null);
                return;
            }
            setThread({ ...(snap.data() as Omit<ConsultationThread, 'id'>), id: threadId });
        });
    }, [guided, user?.guidedClientId, consultantId]);

    useEffect(() => {
        if (!guided || !activeHouseholdId) {
            setPlanMeta(null);
            return;
        }
        return onSnapshot(doc(db, 'householdPlanMeta', activeHouseholdId), (snap) => {
            if (!snap.exists()) {
                setPlanMeta(null);
                return;
            }
            setPlanMeta(parseHouseholdPlanMeta(snap.data()));
        });
    }, [guided, activeHouseholdId]);

    return useMemo(() => {
        if (!guided) return null;
        if (!accessLoaded || !consultantId) return null;

        const name = consultantProfile
            ? getUserDisplayName(consultantProfile)
            : t('userTypeNutritionConsultant');
        const roleLabel = consultantProfile?.consultantCertification?.trim()
            || (consultantProfile
                ? getUserTypeLabel(language, consultantProfile.userType)
                : t('certifiedDietitian'));

        const consultantUpdatedPlan = Boolean(
            planMeta
            && planMeta.lastUpdatedByUid === consultantId
            && planMeta.lastUpdatedAt,
        );

        return {
            consultantId,
            name,
            roleLabel,
            avatarUrl: consultantProfile?.avatarUrl,
            phraseOfTheDay: phraseText ?? undefined,
            lastPlanUpdateAt: consultantUpdatedPlan ? planMeta?.lastUpdatedAt : undefined,
            hasUnreadMessages: thread ? threadHasUnreadForClient(thread) : false,
            isReady: true,
        };
    }, [
        accessLoaded,
        consultantId,
        consultantProfile,
        guided,
        language,
        phraseText,
        planMeta,
        t,
        thread,
    ]);
}

export { getInitials };
